// ═══════════════════════════════════════════════════════════════════════════════
// Employer task lists — section headings with weekday (timezone + UI language)
// ═══════════════════════════════════════════════════════════════════════════════

import { intlLocaleForLanguage, type Language } from '@fdh/shared';

const addCalendarDaysToYmd = (ymd: string, deltaDays: number): string => {
  const [yRaw, mRaw, dRaw] = ymd.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const d = Number(dRaw);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + deltaDays);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const weekdayLocaleForLanguage = (language: Language): string => intlLocaleForLanguage(language);

export const formatWeekdayLongForYmd = (
  ymd: string,
  timezone: string,
  language: Language,
): string => {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return '';
  }
  const y = Number(match[1]);
  const mm = Number(match[2]);
  const dd = Number(match[3]);
  const utcNoon = Date.UTC(y, mm - 1, dd, 12, 0, 0);
  return new Intl.DateTimeFormat(weekdayLocaleForLanguage(language), {
    weekday: 'long',
    timeZone: timezone,
  }).format(new Date(utcNoon));
};

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export const buildEmployerGroupedTaskDateHeading = (options: {
  dateYmd: string;
  todayYmd: string;
  tomorrowYmd: string | null;
  timezone: string;
  language: Language;
  t: TranslateFn;
}): string => {
  const { dateYmd, todayYmd, tomorrowYmd, timezone, language, t } = options;
  const weekday = formatWeekdayLongForYmd(dateYmd, timezone, language);
  if (dateYmd === todayYmd) {
    return `${dateYmd} (${t('task.history.relativeToday')}·${weekday})`;
  }
  if (dateYmd === addCalendarDaysToYmd(todayYmd, -1)) {
    return `${dateYmd} (${t('task.history.relativeYesterday')}·${weekday})`;
  }
  if (tomorrowYmd && dateYmd === tomorrowYmd) {
    return `${dateYmd} (${t('task.history.relativeTomorrow')}·${weekday})`;
  }
  return `${dateYmd} (${weekday})`;
};
